import type { ElementType, ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import type { DesignSystemAs, DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";

const TEXT_VARIANT_CLASSES = {
  inherit: "",
  overline: "text-overline",
  "overline-emphasis": "text-overline-emphasis",
  caption: "text-caption",
  "caption-emphasis": "text-caption-emphasis",
  body: "text-body",
  "body-spacious": "text-body-spacious",
  "body-emphasis": "text-body-emphasis",
  "figure-md": "text-figure-md",
  "figure-lg": "text-figure-lg",
  "figure-xl": "text-figure-xl",
} as const;

const TEXT_TONE_CLASSES = {
  inherit: "",
  default: "text-text-primary",
  secondary: "text-text-secondary",
  tertiary: "text-text-tertiary",
  brand: "text-text-brand",
  "brand-secondary": "text-text-brand-secondary",
  success: "text-text-success",
  warning: "text-text-warning",
  danger: "text-text-danger",
  "danger-secondary": "text-text-danger-secondary",
  inverse: "text-text-default-inverse",
  disabled: "text-text-disabled",
} as const;

const TEXT_MEASURE_CLASSES = {
  compact: "max-w-measure-compact",
  default: "max-w-measure-default",
  reading: "max-w-measure-reading",
  wide: "max-w-measure-wide",
} as const;

const TEXT_ALIGN_CLASSES = {
  start: "text-start",
  center: "text-center",
  end: "text-end",
} as const;

const TEXT_OVERFLOW_CLASSES = {
  normal: "",
  wrap: "wrap-break-word",
  preserve: "whitespace-pre-line wrap-break-word",
  nowrap: "whitespace-nowrap",
  truncate: "truncate",
  "line-clamp-one": "line-clamp-1",
  "line-clamp-two": "line-clamp-2",
  balance: "text-balance",
} as const;

export type TextVariant = keyof typeof TEXT_VARIANT_CLASSES;
export type TextTone = keyof typeof TEXT_TONE_CLASSES;
export type TextMeasure = keyof typeof TEXT_MEASURE_CLASSES;
export type TextAlign = keyof typeof TEXT_ALIGN_CLASSES;
export type TextOverflow = keyof typeof TEXT_OVERFLOW_CLASSES;

type TextElement = "span" | "p" | "label" | "div";

export type TextProps<TElement extends TextElement = "span"> = {
  children: ReactNode;
  variant?: TextVariant;
  tone?: TextTone;
  measure?: TextMeasure;
  align?: TextAlign;
  overflow?: TextOverflow;
} & DesignSystemAs<TElement> &
  DesignSystemElementProps<TElement> &
  DesignSystemRef<HTMLElementTagNameMap[TElement]>;

export function Text<TElement extends TextElement = "span">({
  as,
  align,
  children,
  measure,
  overflow = "wrap",
  ref,
  tone = "default",
  variant = "body",
  ...nativeProps
}: TextProps<TElement>): ReactNode {
  const Element = (as ?? "span") as ElementType;

  return (
    <Element
      {...nativeProps}
      ref={ref}
      className={twJoin(
        TEXT_VARIANT_CLASSES[variant],
        TEXT_TONE_CLASSES[tone],
        TEXT_OVERFLOW_CLASSES[overflow],
        align && TEXT_ALIGN_CLASSES[align],
        measure && TEXT_MEASURE_CLASSES[measure],
      )}
    >
      {children}
    </Element>
  );
}
